<template>
  <section class="admin-content" id="auth-code-statistics">
    <Navbar />
    <main class="admin-main">

      <!-- Page Header -->
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center justify-content-between text-white" style="padding: 32px 0;">
            <div class="d-flex align-items-center">
              <div class="header-icon-wrap mr-3">
                <i class="fe fe-bar-chart-2" style="font-size: 22px;"></i>
              </div>
              <div>
                <h4 class="mb-0 font-weight-bold">Authorization Code Statistics</h4>
                <small style="opacity: 0.8;">Facility-level referral approval report</small>
              </div>
            </div>
            <div class="d-flex align-items-center" style="gap: 8px;">
              <TodayTasks />
              <button class="btn filter-toggle-btn" @click="filterOpen = !filterOpen">
                <i class="fe mr-1" :class="filterOpen ? 'fe-x' : 'fe-filter'"></i>
                {{ filterOpen ? 'Close Filter' : 'Filter' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <section style="margin-top: -20px;">
        <div class="container pb-5">

          <!-- Filter Card -->
          <div v-show="filterOpen" class="card shadow-sm form-card mb-4">
            <div class="form-card-header">
              <div class="section-number"><i class="fe fe-filter" style="font-size:12px;"></i></div>
              <h6 class="mb-0 font-weight-bold text-white">Filter Parameters</h6>
            </div>
            <div class="card-body px-4 py-4">
              <div class="form-row">

                <div class="form-group col-md-4">
                  <label class="small font-weight-bold text-secondary">Status</label>
                  <select class="form-control" v-model="status">
                    <option value="">All Statuses</option>
                    <option value="approved">Approved</option>
                    <option value="pending">Pending</option>
                    <option value="rejected">Rejected</option>
                    <option value="primary_approval">Primary Approval</option>
                  </select>
                </div>

                <div class="form-group col-md-4">
                  <label class="small font-weight-bold text-secondary">Type</label>
                  <select class="form-control" v-model="isCorridor">
                    <option value="">All</option>
                    <option value="false">Standard Referral</option>
                    <option value="true">Corridor / Portability</option>
                  </select>
                </div>

                <div class="form-group col-md-4">
                  <label class="small font-weight-bold text-secondary">Programme</label>
                  <v-select v-model="sector" :options="sectors" multiple placeholder="All programmes…" />
                </div>

                <div class="form-group col-md-4">
                  <label class="small font-weight-bold text-secondary">Level of Care (initiating facility)</label>
                  <select class="form-control" v-model="phcType">
                    <option value="">All</option>
                    <option v-for="t in careLevels" :key="t" :value="t">{{ t }}</option>
                  </select>
                </div>

                <div class="form-group col-md-4">
                  <label class="small font-weight-bold text-secondary">Ownership (initiating facility)</label>
                  <select class="form-control" v-model="phcGeneral">
                    <option value="">All</option>
                    <option value="Public">Public</option>
                    <option value="Private">Private</option>
                  </select>
                </div>

                <div class="form-group col-md-4">
                  <label class="small font-weight-bold text-secondary">Start Date</label>
                  <input type="date" class="form-control" v-model="from" />
                </div>

                <div class="form-group col-md-4">
                  <label class="small font-weight-bold text-secondary">End Date</label>
                  <input type="date" class="form-control" v-model="to" />
                </div>

              </div>

              <div class="d-flex flex-wrap mt-2" style="gap: 8px;">
                <button class="btn btn-submit" @click="load" :disabled="isLoading">
                  <i class="fe fe-bar-chart-2 mr-1"></i> Get Statistics
                </button>
                <button class="btn btn-outline-danger" @click="clearFilters">
                  <i class="fe fe-x-circle mr-1"></i> Clear
                </button>
                <button class="btn btn-success ml-md-auto" @click="exportReport" :disabled="exporting"
                  title="Summary, a sheet per breakdown (facility, facility type, LGA, programme) and every request, with these filters">
                  <i class="fe fe-file-text mr-1"></i> Authorization Report (Excel)
                </button>
                <button class="btn btn-outline-success" @click="exportRequests" :disabled="exporting"
                  title="Every request matching these filters, as an Excel file">
                  <span v-if="exporting"><i class="mdi mdi-loading mdi-spin mr-1"></i> Exporting…</span>
                  <span v-else><i class="fe fe-download mr-1"></i> Export Requests (Excel)</span>
                </button>
                <button class="btn btn-outline-secondary" @click="exportCsv" :disabled="exporting"
                  title="Every request matching these filters, as a CSV file">
                  <i class="fe fe-download mr-1"></i> Export CSV
                </button>
              </div>
            </div>
          </div>

          <!-- Summary Cards -->
          <div class="d-flex justify-content-end mb-2">
            <div class="card-download-tools">
              <button class="btn-download" title="Download as image" @click="downloadElementImage('summaryCardsRef', 'authorization-code-summary')">
                <i class="fe fe-image"></i>
              </button>
              <button class="btn-download" title="Download as CSV" @click="downloadSummaryCsv">
                <i class="fe fe-download"></i>
              </button>
            </div>
          </div>
          <div class="row mb-4" ref="summaryCardsRef">
            <div class="col-6 col-md-2" v-for="card in summaryCards" :key="card.label">
              <div class="stat-card">
                <div class="stat-icon-wrap" :style="{ background: card.bg }">
                  <i :class="`fe ${card.icon}`" :style="{ color: card.color }"></i>
                </div>
                <div>
                  <p class="stat-label">{{ card.label }}</p>
                  <h3 class="stat-value">{{ card.value | numeral(0, 0) }}</h3>
                </div>
              </div>
            </div>
          </div>

          <!-- Turnaround times against the targets (refreshed with Get Statistics) -->
          <TurnaroundPanel :from="tatFrom" :to="tatTo" />

          <!-- Date Trend Chart -->
          <div class="card shadow-sm table-card mb-4">
            <div class="form-card-header">
              <div class="section-number"><i class="fe fe-trending-up" style="font-size:12px;"></i></div>
              <h6 class="mb-0 font-weight-bold text-white">Requests by Date</h6>
            </div>
            <div class="card-body p-3">
              <div v-if="isLoading" class="text-center py-4 text-muted">
                <i class="fe fe-loader" style="font-size:1.5rem; opacity:0.4;"></i>
                <p class="mt-2 mb-0 small">Loading chart…</p>
              </div>
              <div v-else-if="!chartSeries[0].data.length" class="text-center py-4 text-muted">
                <i class="fe fe-inbox" style="font-size:2rem; opacity:0.3;"></i>
                <p class="mt-2 mb-0 small">No data — apply filters and click Get Statistics.</p>
              </div>
              <apexchart
                v-else
                type="bar"
                height="280"
                :options="chartOptions"
                :series="chartSeries"
              />
            </div>
          </div>

          <!-- Breakdowns: initiating / receiving facility, facility type, LGA, programme -->
          <div class="card shadow-sm table-card">
            <div class="form-card-header flex-wrap">
              <div class="section-number"><i class="fe fe-list" style="font-size:12px;"></i></div>
              <h6 class="mb-0 font-weight-bold text-white">Breakdown by {{ breakdownLabel.toLowerCase() }}</h6>
              <span v-if="breakdownRows.length" class="badge badge-light ml-auto mr-2" style="border-radius:20px;">
                {{ breakdownRows.length }} rows
              </span>
              <div class="card-download-tools card-download-tools--dark" :class="{ 'ml-auto': !breakdownRows.length }">
                <button class="btn-download" title="Download as image" @click="downloadElementImage('byProviderRef', 'authorization-breakdown-' + breakdownBy)">
                  <i class="fe fe-image"></i>
                </button>
                <button class="btn-download" title="Download as CSV"
                  @click="downloadCsv([breakdownLabel, 'Requests', 'Approved', 'Pending', 'of which primary approval', 'Rejected', 'Code Used', 'Approval rate %'], breakdownRows.map(function (r) { return [r.name, r.total, r.approved, r.pending, r.primary_approval, r.rejected, r.used, r.approval_rate === null ? '' : r.approval_rate]; }), 'authorization-breakdown-' + breakdownBy)">
                  <i class="fe fe-download"></i>
                </button>
              </div>
            </div>
            <div class="d-flex flex-wrap align-items-center px-4 py-3" style="gap: 8px; border-bottom: 1px solid #f1f5f9;">
              <button v-for="b in breakdownTabs" :key="b.key" type="button" class="btn btn-sm"
                :class="breakdownBy === b.key ? 'btn-submit' : 'btn-outline-secondary'" @click="breakdownBy = b.key">{{ b.label }}</button>
            </div>
            <div class="table-responsive" ref="byProviderRef">
              <table class="table align-td-middle mb-0">
                <thead style="background:#f8fafc;">
                  <tr>
                    <th class="table-th">#</th>
                    <th class="table-th">{{ breakdownLabel }}</th>
                    <th class="table-th text-center">Requests</th>
                    <th class="table-th text-center">Approved</th>
                    <th class="table-th text-center" title="Includes requests with primary approval (corridor)">Pending</th>
                    <th class="table-th text-center">Rejected</th>
                    <th class="table-th text-center">Code Used</th>
                    <th class="table-th text-center" title="Approved ÷ (approved + rejected)">Approval Rate</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, i) in breakdownRows" :key="breakdownBy + '-' + (row.key === null ? 'none' : row.key)" class="table-row">
                    <td class="px-4 py-3 text-muted small">{{ i + 1 }}</td>
                    <td class="py-3 font-weight-bold" style="font-size:0.86rem;">{{ row.name }}</td>
                    <td class="py-3 text-center">
                      <span class="badge badge-soft-info" style="border-radius:20px; padding:4px 12px;">
                        {{ row.total | numeral(0, 0) }}
                      </span>
                    </td>
                    <td class="py-3 text-center text-success font-weight-bold">{{ row.approved | numeral(0, 0) }}</td>
                    <td class="py-3 text-center text-warning">
                      {{ row.pending | numeral(0, 0) }}
                      <small v-if="row.primary_approval" class="d-block text-muted">{{ row.primary_approval }} primary approval</small>
                    </td>
                    <td class="py-3 text-center text-danger">{{ row.rejected | numeral(0, 0) }}</td>
                    <td class="py-3 text-center">{{ row.used | numeral(0, 0) }}</td>
                    <td class="py-3 text-center">{{ row.approval_rate === null ? '—' : row.approval_rate + '%' }}</td>
                  </tr>
                  <tr v-if="!breakdownRows.length">
                    <td colspan="8" class="text-center py-5 text-muted">
                      <i class="fe fe-inbox" style="font-size:2rem; opacity:0.3;"></i>
                      <p class="mt-2 mb-0">Apply filters and click Get Statistics.</p>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Top Performers (authorization_codes.treated_by) -->
          <div class="card shadow-sm table-card mt-4">
            <div class="form-card-header">
              <div class="section-number"><i class="fe fe-award" style="font-size:12px;"></i></div>
              <h6 class="mb-0 font-weight-bold text-white">Top Performers</h6>
              <span v-if="topPerformers.length" class="badge badge-light ml-auto mr-2" style="border-radius:20px;">
                {{ topPerformers.length }} staff
              </span>
              <div class="card-download-tools card-download-tools--dark">
                <button class="btn-download" title="Download as image" @click="downloadElementImage('topPerformersRef', 'authorization-top-performers')">
                  <i class="fe fe-image"></i>
                </button>
                <button class="btn-download" title="Download as CSV"
                  @click="downloadCsv(['Staff', 'Job Title', 'Codes Approved'], topPerformers.map(function (r) { return [r.name || ('User ' + r.user_id), r.job_title || '', r.count]; }), 'authorization-top-performers')">
                  <i class="fe fe-download"></i>
                </button>
              </div>
            </div>
            <div class="table-responsive" ref="topPerformersRef">
              <table class="table align-td-middle mb-0">
                <thead style="background:#f8fafc;">
                  <tr>
                    <th class="table-th">#</th>
                    <th class="table-th">Staff</th>
                    <th class="table-th">Job Title</th>
                    <th class="table-th text-center">Codes Approved</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, i) in topPerformers" :key="row.user_id" class="table-row">
                    <td class="px-4 py-3 text-muted small">{{ i + 1 }}</td>
                    <td class="py-3 font-weight-bold" style="font-size:0.86rem;">{{ row.name || ('User ' + row.user_id) }}</td>
                    <td class="py-3 text-muted small">{{ row.job_title || '—' }}</td>
                    <td class="py-3 text-center">
                      <span class="badge badge-soft-success" style="border-radius:20px; padding:4px 12px;">
                        {{ row.count | numeral(0, 0) }}
                      </span>
                    </td>
                  </tr>
                  <tr v-if="!topPerformers.length">
                    <td colspan="4" class="text-center py-5 text-muted">
                      <i class="fe fe-inbox" style="font-size:2rem; opacity:0.3;"></i>
                      <p class="mt-2 mb-0">No codes approved in this period.</p>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

        </div>

        <div class="vld-parent">
          <loading :active.sync="isLoading" loader="dots" :can-cancel="false" :is-full-page="fullPage" />
        </div>
      </section>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import TodayTasks from "@/components/TodayTasks.vue";
import TurnaroundPanel from "@/views/reports/components/TurnaroundPanel.vue";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import VueApexCharts from "vue-apexcharts";
import moment from "moment";
import { downloadServerExcel } from "@/service/serverExcel";
import SectorsJson from "@/json/sectors.json";
moment().format();

function chartToolbar(filename) {
  return {
    show: true,
    tools: { download: true, selection: false, zoom: false, zoomin: false, zoomout: false, pan: false, reset: false },
    export: {
      csv: { filename: filename },
      svg: { filename: filename },
      png: { filename: filename },
    },
  };
}

export default {
  components: { TodayTasks, TurnaroundPanel, Navbar, Loading, apexchart: VueApexCharts },
  data() {
    return {
      isLoading: false,
      exporting: false,
      fullPage: true,
      filterOpen: false,
      tatFrom: moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD"),
      tatTo: moment().subtract(1, "month").endOf("month").format("YYYY-MM-DD"),
      from: moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD"),
      to: moment().subtract(1, "month").endOf("month").format("YYYY-MM-DD"),
      status: "",
      isCorridor: "",
      sector: [],
      sectors: SectorsJson,
      phcType: "",
      phcGeneral: "",
      careLevels: ["Primary", "Secondary", "Tertiary", "Telemedicine"],
      breakdowns: {},
      breakdownBy: "initiating_facility",
      breakdownTabs: [
        { key: "initiating_facility", label: "Initiating facility" },
        { key: "receiving_facility", label: "Receiving facility" },
        { key: "facility_type", label: "Facility type" },
        { key: "lga", label: "LGA" },
        { key: "programme", label: "Programme" },
      ],
      summary: { total: 0, approved: 0, pending: 0, rejected: 0, primary_approval: 0, code_used: 0 },
      byProvider: [],
      byDate: [],
      topPerformers: [],
    };
  },
  computed: {
    breakdownRows() { return this.breakdowns[this.breakdownBy] || []; },
    breakdownLabel() { return (this.breakdownTabs.find((b) => b.key === this.breakdownBy) || {}).label || ""; },
    summaryCards() {
      return [
        { label: "Total",            value: this.summary.total,            icon: "fe-grid",       bg: "#e8f4ff", color: "#2563eb" },
        { label: "Approved",         value: this.summary.approved,         icon: "fe-check-circle",bg: "#ecfdf5", color: "#059669" },
        { label: "Pending",          value: this.summary.pending,          icon: "fe-clock",      bg: "#fff7e6", color: "#d97706" },
        { label: "Rejected",         value: this.summary.rejected,         icon: "fe-x-circle",   bg: "#fff1f2", color: "#e11d48" },
        { label: "Primary Approval", value: this.summary.primary_approval, icon: "fe-shield",     bg: "#f5f3ff", color: "#7c3aed" },
        { label: "Code Used",        value: this.summary.code_used,        icon: "fe-zap",        bg: "#f0fdf4", color: "#16a34a" },
      ];
    },
    chartOptions() {
      return {
        chart: { type: "bar", toolbar: chartToolbar("authorization-requests-by-date"), fontFamily: "inherit" },
        plotOptions: { bar: { borderRadius: 3, columnWidth: "60%" } },
        dataLabels: { enabled: true },
        xaxis: {
          categories: this.byDate.map((d) => d.date),
          labels: { style: { fontSize: "10px", colors: "#64748b" }, rotate: -30 },
        },
        yaxis: { labels: { style: { fontSize: "11px", colors: "#64748b" } } },
        colors: ["#2563eb", "#059669", "#e11d48"],
        legend: { position: "top" },
        grid: { borderColor: "#f1f5f9", strokeDashArray: 4 },
        tooltip: { y: { formatter: (v) => v.toLocaleString() } },
      };
    },
    chartSeries() {
      return [
        { name: "Total",    data: this.byDate.map((d) => Number(d.total    || 0)) },
        { name: "Approved", data: this.byDate.map((d) => Number(d.approved || 0)) },
        { name: "Rejected", data: this.byDate.map((d) => Number(d.rejected || 0)) },
      ];
    },
  },
  methods: {
    agencyId() {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      return user.institutional_id || user.agency_id;
    },
    load() {
      this.tatFrom = this.from;
      this.tatTo = this.to;
      this.isLoading = true;
      this.axios
        .post("/api/v1/auth/authorization-code/statistics", {
          agency_id:  this.agencyId(),
          from:       this.from,
          to:         this.to,
          status:     this.status || undefined,
          is_corridor: this.isCorridor !== "" ? this.isCorridor : undefined,
          ...this.reportFilters(),
        })
        .then(({ data }) => {
          this.summary    = data.summary;
          this.breakdowns = data.breakdowns || {};
          this.byProvider = data.by_provider;
          this.byDate     = data.by_date;
          this.topPerformers = data.top_performers || [];
          this.$toasted.success("Loaded successfully", { position: "top-center", duration: 2500 });
        })
        .catch((err) => {
          console.error(err);
          this.$toasted.error("Failed to load statistics", { position: "top-center", duration: 3000 });
        })
        .finally(() => { this.isLoading = false; });
    },
    reportFilters() {
      return {
        sector: this.sector && this.sector.length ? this.sector : undefined,
        phc_type: this.phcType || undefined,
        phc_general: this.phcGeneral || undefined,
      };
    },
    // The report workbook (authorization-code/statistics, format=excel): summary, one sheet per breakdown, every request.
    async exportReport() {
      this.exporting = true;
      try {
        await downloadServerExcel(this.axios, {
          method: "post",
          url: "/api/v1/auth/authorization-code/statistics",
          data: {
            format: "excel",
            agency_id: this.agencyId(),
            from: this.from,
            to: this.to,
            status: this.status || undefined,
            is_corridor: this.isCorridor !== "" ? this.isCorridor : undefined,
            ...this.reportFilters(),
          },
        }, `authorization-report_${this.from || "all"}_${this.to || "all"}.xlsx`);
        this.$toasted.success("Report downloaded", { position: "top-center", duration: 3000 });
      } catch (error) {
        this.$toasted.error(error.message, { position: "top-center", duration: 4000 });
      } finally {
        this.exporting = false;
      }
    },
    // The request list behind these numbers, built by the API (authorization_code/{agency}, format=excel).
    async exportRequests() {
      this.exporting = true;
      try {
        await downloadServerExcel(this.axios, {
          method: "get",
          url: `/api/v1/auth/authorization_code/${this.agencyId()}`,
          params: {
            format: "excel",
            status: this.status || undefined,
            // The list endpoint defaults to standard referrals only; "all" includes corridor requests.
            is_corridor: this.isCorridor === "" ? "all" : (this.isCorridor === "true" ? 1 : 0),
            from: this.from || undefined,
            to: this.to || undefined,
          },
        }, `authorization_codes_${this.from || "all"}_${this.to || "all"}.xlsx`);
        this.$toasted.success("Excel file downloaded", { position: "top-center", duration: 3000 });
      } catch (error) {
        this.$toasted.error(error.message, { position: "top-center", duration: 4000 });
      } finally {
        this.exporting = false;
      }
    },
    // Same pattern as View Encounters: the API builds the CSV (format=csv) from every request matching these filters.
    exportCsv() {
      this.exporting = true;
      this.axios
        .get(`/api/v1/auth/authorization_code/${this.agencyId()}`, {
          params: {
            format: "csv",
            status: this.status || undefined,
            // The list endpoint defaults to standard referrals only; "all" includes corridor requests.
            is_corridor: this.isCorridor === "" ? "all" : (this.isCorridor === "true" ? 1 : 0),
            from: this.from || undefined,
            to: this.to || undefined,
          },
        })
        .then((response) => {
          const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `authorization_codes_${new Date().toISOString().slice(0, 10)}.csv`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          this.$toasted.success("CSV downloaded successfully", { position: "top-center", duration: 3000 });
        })
        .catch((error) => {
          console.error(error);
          this.$toasted.error("Failed to export CSV", { position: "top-center", duration: 3000 });
        })
        .finally(() => { this.exporting = false; });
    },
    clearFilters() {
      this.status     = "";
      this.isCorridor = "";
      this.sector     = [];
      this.phcType    = "";
      this.phcGeneral = "";
      this.breakdowns = {};
      this.from       = moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD");
      this.to         = moment().subtract(1, "month").endOf("month").format("YYYY-MM-DD");
      this.summary    = { total: 0, approved: 0, pending: 0, rejected: 0, primary_approval: 0, code_used: 0 };
      this.byProvider = [];
      this.byDate     = [];
      this.topPerformers = [];
    },
    async downloadElementImage(refName, filename) {
      try {
        const el = this.$refs[refName];
        const dataUrl = await this.$html2canvas(el, { type: "dataURL" });
        const link = document.createElement("a");
        link.href = dataUrl;
        link.setAttribute("download", `${filename}.png`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error(error);
        this.$toasted.error("Could not generate image.", { position: "top-center", duration: 3000 });
      }
    },
    downloadCsv(headers, rows, filename) {
      const escapeCell = function (value) {
        const str = value === null || value === undefined ? "" : String(value);
        return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
      };
      const lines = [headers, ...rows].map(function (row) { return row.map(escapeCell).join(","); });
      const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${filename}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    downloadSummaryCsv() {
      const headers = ["Metric", "Value"];
      const rows = this.summaryCards.map(function (c) { return [c.label, c.value]; });
      this.downloadCsv(headers, rows, "authorization-code-summary");
    },
  },
  created() {
    this.load();
  },
};
</script>

<style scoped>
.page-header {
  background: linear-gradient(135deg, #1e3a5f 0%, #7c3aed 100%);
  padding-bottom: 40px;
}

.header-icon-wrap {
  width: 48px; height: 48px;
  background: rgba(255,255,255,0.15);
  border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}

.filter-toggle-btn {
  background: rgba(255,255,255,0.15);
  border: 1px solid rgba(255,255,255,0.3);
  color: #fff; border-radius: 8px;
  font-weight: 600; font-size: 0.85rem;
  padding: 7px 16px; transition: background 0.15s;
}
.filter-toggle-btn:hover { background: rgba(255,255,255,0.25); color: #fff; }

.stat-card {
  display: flex; align-items: center; gap: 12px;
  background: #fff; border: 1px solid #e9ecef;
  border-radius: 12px; padding: 16px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
  margin-bottom: 16px;
}

.stat-icon-wrap {
  width: 40px; height: 40px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  font-size: 1.1rem; flex-shrink: 0;
}

.stat-label {
  font-size: 0.68rem; text-transform: uppercase;
  letter-spacing: 0.05em; color: #94a3b8;
  margin-bottom: 2px; font-weight: 600;
}

.stat-value {
  font-size: 1.2rem; font-weight: 700;
  color: #1e293b; margin-bottom: 0; line-height: 1.2;
}

.form-card, .table-card { border-radius: 12px; border: 1px solid #e9ecef; overflow: hidden; }

.form-card-header {
  display: flex; align-items: center; gap: 12px;
  padding: 14px 20px;
  background: linear-gradient(135deg, #1e3a5f 0%, #7c3aed 100%);
}

.section-number {
  width: 28px; height: 28px; background: rgba(255,255,255,0.2);
  border-radius: 8px; display: flex; align-items: center; justify-content: center;
  color: #fff; flex-shrink: 0;
}

.table-th {
  font-size: 0.73rem; text-transform: uppercase; letter-spacing: 0.04em;
  color: #64748b; font-weight: 600;
  border-bottom: 2px solid #e2e8f0; padding: 12px 16px;
}

.table-row:hover { background: #f8fafc; }

.btn-submit {
  background: linear-gradient(135deg, #1e3a5f 0%, #7c3aed 100%);
  border: none; color: #fff; border-radius: 8px;
  font-weight: 600; padding: 8px 20px;
}

.card-download-tools {
  display: flex; gap: 4px;
}

.card-download-tools--dark {
  margin-left: auto;
}

.btn-download {
  display: flex; align-items: center; justify-content: center;
  width: 30px; height: 30px;
  border-radius: 6px; border: 1px solid #e2e8f0;
  background: #fff; color: #64748b;
  font-size: 0.8rem; cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.btn-download:hover { background: #f1f5f9; color: #1e3a5f; }

.card-download-tools--dark .btn-download {
  border-color: rgba(255,255,255,0.35);
  background: rgba(255,255,255,0.12);
  color: #fff;
}
.card-download-tools--dark .btn-download:hover {
  background: rgba(255,255,255,0.25);
  color: #fff;
}
</style>
